import { Component, OnInit, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { catchError, filter, finalize, forkJoin, map, of, startWith, take } from 'rxjs';
import { AuthService } from '../../../core/auth/auth.service';
import { CurrentUserService } from '../../../core/auth/current-user.service';
import { NotificationItem, NotificationType, TestNotificationResponse } from '../../../core/models/product.models';
import { User } from '../../../core/models/user.models';
import { NotificationsService } from '../../../core/services/notifications.service';
import { FirebaseWebPushService } from '../../../core/services/firebase-web-push.service';
import { httpErrorMessage } from '../../../core/utils/http-error-message';
import { BusinessService, BusinessStore } from '../../../core/services/business.service';
import { BusinessLocationContextService } from '../../../core/services/business-location-context.service';
import { UsersService } from '../../../core/services/users.service';

@Component({
  selector: 'app-header',
  imports: [FormsModule, RouterLink],
  templateUrl: './header.html',
  styleUrl: './header.scss',
})
export class Header implements OnInit {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly notifications = inject(NotificationsService);
  private readonly usersService = inject(UsersService);
  readonly currentUser = inject(CurrentUserService);
  private readonly businesses = inject(BusinessService);
  private readonly businessUsers = inject(UsersService);
  readonly locationContext = inject(BusinessLocationContextService);
  readonly webPush = inject(FirebaseWebPushService);
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  readonly notificationTypes: Array<{ value: NotificationType; label: string }> = [
    { value: 1, label: 'Product' },
    { value: 2, label: 'Order' },
    { value: 3, label: 'Chat' },
    { value: 4, label: 'System' },
  ];

  notificationUsers: User[] = [];
  savedNotifications: NotificationItem[] = [];
  showNotificationTester = false;
  isUsersLoading = false;
  isNotificationsLoading = false;
  isSendingNotification = false;
  notificationError = '';
  notificationSuccess = '';
  notificationForm = this.emptyNotificationForm();
  businessLocations: BusinessStore[] = [];
  isLocationsLoading = false;

  ngOnInit(): void {
    if (!this.isBusinessPortal) return;
    // Location access may be changed by an owner while this user still has an
    // older JWT/session in the browser. Refresh Identity first so the portal
    // always filters locations using the latest database assignments.
    this.auth.refreshSession().pipe(
      take(1),
      catchError(() => of(null)),
      finalize(() => this.loadBusinessLocations()),
    ).subscribe();
  }

  selectBusinessLocation(locationId: string): void {
    this.locationContext.select(locationId);
  }

  get isBusinessPortal(): boolean {
    return this.currentUrl().startsWith('/business');
  }

  get portalEyebrow(): string {
    return this.isBusinessPortal ? 'Business portal' : 'Platform admin';
  }

  get portalTitle(): string {
    return this.isBusinessPortal ? 'Products, inventory, and orders' : 'Access, teams, and operations';
  }

  get profileRoute(): string {
    return this.isBusinessPortal ? '/business/dashboard' : '/admin/users';
  }

  get profileRole(): string {
    if (this.currentUser.isBusinessOwner()) return 'Business owner';
    const role = this.currentUser.user()?.role;
    return role || (this.isBusinessPortal ? 'Business user' : 'Superadmin');
  }

  get profileInitials(): string {
    const user = this.currentUser.user();
    const source = user?.name || [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'Business user';
    const words = source.trim().split(/\s+/).filter(Boolean);
    return (words.length > 1 ? `${words[0][0]}${words[words.length - 1][0]}` : words[0]?.slice(0, 2) || 'BU').toUpperCase();
  }

  openNotificationTester(): void {
    this.showNotificationTester = true;
    this.notificationError = '';
    this.notificationSuccess = '';
    this.loadNotifications();
    if (this.notificationUsers.length) return;

    this.isUsersLoading = true;
    this.usersService.list(100).pipe(
      take(1),
      finalize(() => (this.isUsersLoading = false)),
    ).subscribe({
      next: (users) => {
        const signedInUser = this.currentUser.user();
        const activeUsers = users.filter((user) => user.status !== 'Suspended');
        this.notificationUsers = signedInUser && !activeUsers.some((user) => user.id === signedInUser.id)
          ? [signedInUser, ...activeUsers]
          : activeUsers;
        const currentUserId = signedInUser?.id ?? '';
        if (!this.notificationForm.userId && currentUserId) this.notificationForm.userId = currentUserId;
      },
      error: (error: unknown) => {
        this.notificationError = httpErrorMessage(error, 'Unable to load users.');
      },
    });
  }

  closeNotificationTester(): void {
    if (this.isSendingNotification) return;
    this.showNotificationTester = false;
  }

  sendTestNotification(): void {
    if (this.isSendingNotification) return;

    const userId = this.notificationForm.userId;
    const title = this.notificationForm.title.trim();
    const message = this.notificationForm.message.trim();
    const dataJson = this.notificationForm.dataJson.trim();
    if (!userId || !title || !message) {
      this.notificationError = 'Select a user and enter a title and message.';
      return;
    }
    if (dataJson && !this.isJsonObject(dataJson)) {
      this.notificationError = 'Additional data must be a valid JSON object.';
      return;
    }

    this.isSendingNotification = true;
    this.notificationError = '';
    this.notificationSuccess = '';
    this.notifications.sendTest({
      userId,
      title,
      message,
      type: this.notificationForm.type,
      dataJson: dataJson || null,
    }).pipe(
      take(1),
      finalize(() => (this.isSendingNotification = false)),
    ).subscribe({
      next: (response) => {
        this.isSendingNotification = false;
        this.notificationSuccess = this.deliveryMessage(response);
        this.notificationForm = this.emptyNotificationForm(userId);
        if (userId === this.currentUser.user()?.id) this.loadNotifications();
      },
      error: (error: unknown) => {
        this.isSendingNotification = false;
        this.notificationError = httpErrorMessage(error, 'Unable to send test notification.');
      },
    });
  }

  async enableThisBrowser(): Promise<void> {
    this.notificationError = '';
    this.notificationSuccess = '';
    try {
      await this.webPush.enable();
      const currentUserId = this.currentUser.user()?.id ?? '';
      if (currentUserId) this.notificationForm.userId = currentUserId;
      this.notificationSuccess = 'This browser is registered. Send a test to the signed-in user.';
    } catch (error) {
      this.notificationError = error instanceof Error ? error.message : 'Unable to enable browser notifications.';
    }
  }

  webPushStatus(): string {
    const labels: Record<string, string> = {
      Ready: 'Not enabled',
      Enabling: 'Enabling...',
      Enabled: 'Browser registered',
      Denied: 'Permission denied',
      Unsupported: 'Not supported',
      NotConfigured: 'Configuration required',
      Error: 'Registration failed',
    };
    return labels[this.webPush.status()] ?? this.webPush.status();
  }

  markNotificationRead(notification: NotificationItem): void {
    if (notification.isRead) return;

    this.notifications.markRead(notification.id).pipe(take(1)).subscribe({
      next: () => {
        this.savedNotifications = this.savedNotifications.map((item) =>
          item.id === notification.id ? { ...item, isRead: true, readAt: new Date().toISOString() } : item,
        );
      },
      error: (error: unknown) => {
        this.notificationError = httpErrorMessage(error, 'Unable to mark notification as read.');
      },
    });
  }

  notificationDate(value: string): string {
    return new Date(value).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  }

  logout(): void {
    this.auth.logout();
    void this.router.navigate([this.isBusinessPortal ? '/login' : '/admin-login']);
  }

  private loadBusinessLocations(): void {
    this.isLocationsLoading = true;
    this.businesses.stores('business', true).pipe(
      take(1),
      finalize(() => (this.isLocationsLoading = false)),
    ).subscribe({
      next: (locations) => {
        const assignedIds = this.currentUser.user()?.businessLocationIds ?? [];
        const isOwner = this.currentUser.isBusinessOwner();
        this.businessLocations = (isOwner
          ? locations
          : locations.filter((location) => assignedIds.includes(location.id)))
          .filter((location) => location.isActive !== false);
        const selected = this.locationContext.selectedLocationId();
        if (!selected || !this.businessLocations.some((location) => location.id === selected)) {
          this.locationContext.select(
            (this.businessLocations.find((location) => location.isPrimary) ?? this.businessLocations[0])?.id ?? null,
          );
        }
        if (isOwner) {
          const missing = this.businessLocations.filter((location) => !assignedIds.includes(location.id));
          if (missing.length) {
            forkJoin(missing.map((location) => this.businessUsers.assignMyBusinessLocation(location.id)))
              .pipe(take(1))
              .subscribe({ next: () => this.auth.refreshSession().pipe(take(1)).subscribe({ error: () => undefined }), error: () => undefined });
          }
        }
      },
      error: () => (this.businessLocations = []),
    });
  }

  private emptyNotificationForm(userId = '') {
    return { userId, title: '', message: '', type: 4 as NotificationType, dataJson: '' };
  }

  private loadNotifications(): void {
    if (this.isNotificationsLoading) return;

    this.isNotificationsLoading = true;
    this.notifications.list().pipe(
      take(1),
      finalize(() => (this.isNotificationsLoading = false)),
    ).subscribe({
      next: (notifications) => {
        this.savedNotifications = notifications.slice(0, 10);
      },
      error: (error: unknown) => {
        this.notificationError = httpErrorMessage(error, 'Unable to load notifications.');
      },
    });
  }

  private isJsonObject(value: string): boolean {
    try {
      const parsed: unknown = JSON.parse(value);
      return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed);
    } catch {
      return false;
    }
  }

  private deliveryMessage(response: TestNotificationResponse): string {
    const delivery = response.pushDelivery;
    if (delivery.status === 'NoDevices') return 'Notification saved, but this user has no registered Firebase device.';
    if (delivery.status === 'Skipped') return `Notification saved. ${delivery.errorMessage ?? 'Firebase delivery was skipped.'}`;
    if (delivery.successCount > 0) {
      return `Notification sent to ${delivery.successCount} of ${delivery.targetCount} registered devices.`;
    }
    return `Notification saved, but Firebase delivery failed. ${delivery.errorMessage ?? ''}`.trim();
  }
}
